import React, { useEffect, useMemo, useState } from 'react';
import { X, Plus, Minus, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import { useCart } from '../../lib/cart';
import { formatCurrency } from '../../lib/utils';
import { Button } from '../ui/Button';
import { ProductDetailModal } from './ProductDetailModal';
import { MenuItem, CartItemOption, MenuCategory } from '../../lib/types';
const DEFAULT_UPSELL_CATEGORY_SLUGS = ['acompanamientos', 'refrescos'];
interface CheckoutUpsellModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue: () => void;
  restaurantId: string | number;
  restaurantName: string;
  restaurantSlug?: string;
  categorySlugs?: string[];
  title?: string;
  subtitle?: string;
}
export function CheckoutUpsellModal({
  isOpen,
  onClose,
  onContinue,
  restaurantId,
  restaurantName,
  restaurantSlug,
  categorySlugs = DEFAULT_UPSELL_CATEGORY_SLUGS,
  title = 'Agrega un acompañamiento',
  subtitle = 'Agrega alguno de estos acompañamientos:'
}: CheckoutUpsellModalProps) {
  const {
    cart,
    addItem,
    updateQuantity
  } = useCart();
  const [categories, setCategories] = useState<MenuCategory[]>([]);
  const [items, setItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [productModalItem, setProductModalItem] = useState<MenuItem | null>(null);
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const fetchUpsell = async () => {
      setLoading(true);
      try {
        const {
          data: cats,
          error: catErr
        } = await supabase.from('menu_categories').select('*').eq('restaurant_id', restaurantId).eq('is_active', true).in('slug', categorySlugs).order('sort_order', {
          ascending: true
        });
        if (catErr) throw catErr;
        const categoryIds = (cats ?? []).map(c => c.id);
        if (categoryIds.length === 0) {
          if (!cancelled) {
            setCategories([]);
            setItems([]);
          }
          return;
        }
        const {
          data: itemsData,
          error: itemsErr
        } = await supabase.from('menu_items').select('*').eq('restaurant_id', restaurantId).eq('is_active', true).in('category_id', categoryIds).order('sort_order', {
          ascending: true
        });
        if (itemsErr) throw itemsErr;
        if (!cancelled) {
          setCategories(cats ?? []);
          setItems(itemsData ?? []);
        }
      } catch (err) {
        console.error('[CheckoutUpsellModal] fetch error:', err);
        if (!cancelled) {
          setCategories([]);
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchUpsell();
    return () => {
      cancelled = true;
    };
  }, [isOpen, restaurantId]);
  // Group items by category slug
  const grouped = useMemo(() => {
    const map: Record<string, {
      category: MenuCategory;
      items: MenuItem[];
    }> = {};
    for (const cat of categories) {
      map[cat.slug] = {
        category: cat,
        items: []
      };
    }
    for (const item of items) {
      const cat = categories.find(c => c.id === item.category_id);
      if (cat && map[cat.slug]) {
        map[cat.slug].items.push(item);
      }
    }
    return map;
  }, [categories, items]);
  // For each item, find the matching cart line (only base, no options)
  const getCartLine = (item: MenuItem) => {
    return cart.items.find(ci => String(ci.menu_item_id) === String(item.id) && (!ci.selected_options || ci.selected_options.length === 0));
  };
  // CRITICAL: Use the cart's stored restaurantId so it matches exactly (string vs number).
  // If the cart already has items, prev.restaurantId !== newItem.restaurant_id (strict) will
  // silently reject the add. We force the same value as what's already in the cart.
  const cartRestaurantId = cart.restaurantId ?? restaurantId;
  const handleQuickAdd = (item: MenuItem) => {
    if (item.is_customizable) {
      setProductModalItem(item);
      return;
    }
    const ok = addItem({
      menu_item_id: item.id,
      restaurant_id: cartRestaurantId as any,
      item_name_snapshot: item.name,
      unit_price_snapshot: item.price,
      quantity: 1,
      notes: '',
      image_url: item.image_url,
      selected_options: [],
      station_code: item.station_code || null
    }, restaurantName, restaurantSlug);
    if (!ok) {
      console.warn('[CheckoutUpsellModal] addItem rejected', {
        cartRestaurantId,
        itemRestaurantId: item.restaurant_id
      });
    }
  };
  const handleProductModalAdd = (item: MenuItem, quantity: number, options: CartItemOption[], notes: string) => {
    addItem({
      menu_item_id: item.id,
      restaurant_id: cartRestaurantId as any,
      item_name_snapshot: item.name,
      unit_price_snapshot: item.price,
      quantity,
      notes,
      image_url: item.image_url,
      selected_options: options,
      station_code: item.station_code || null
    }, restaurantName, restaurantSlug);
    setProductModalItem(null);
  };
  const handleIncrement = (item: MenuItem) => {
    const line = getCartLine(item);
    if (line) {
      updateQuantity(line.id, line.quantity + 1);
    } else {
      handleQuickAdd(item);
    }
  };
  const handleDecrement = (item: MenuItem) => {
    const line = getCartLine(item);
    if (!line) return;
    updateQuantity(line.id, Math.max(0, line.quantity - 1));
  };
  const renderSection = (slug: string, label: string) => {
    const section = grouped[slug];
    if (!section || section.items.length === 0) return null;
    return <div className="mb-5" data-id="element-2593" data-mp="1ow37vmff4u0a">
        <h3 className="text-base font-bold text-gray-900 mb-3" data-id="element-2594" data-mp="1tvfuq76ai12v">{label}</h3>
        <div className="space-y-2" data-id="element-2595" data-mp="1txiq76q7omez">
          {section.items.map(item => {
          const line = getCartLine(item);
          return <div key={item.id} className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-100 rounded-xl" data-id="element-2596" data-mp="3lxqtkg50iedo">
                <div className="flex-1 min-w-0" data-id="element-2597" data-mp="3ozzm9kjq44v">
                  <div className="font-semibold text-sm text-gray-900 truncate" data-id="element-2598" data-mp="3uyyruy7j3f7r">
                    {item.name}
                  </div>
                  {item.description && <div className="text-xs text-gray-500 line-clamp-2 mt-0.5" data-id="element-2599" data-mp="329rhn1ypp16o">
                      {item.description}
                    </div>}
                  <div className="text-sm font-bold text-gray-900 mt-1" data-id="element-2600" data-mp="3gzp8jpo104zk">
                    {formatCurrency(item.price)}
                  </div>
                </div>
                {line && !item.is_customizable ? <div className="flex items-center gap-2 shrink-0" data-id="element-2601" data-mp="2jis20rd3x3q2">
                    <button type="button" onClick={() => handleDecrement(item)} className="w-8 h-8 rounded-full bg-white border border-gray-200 text-gray-700 flex items-center justify-center hover:bg-gray-50" aria-label="Reducir cantidad" data-id="element-2602" data-mp="2jgnxq8baq2p9">
                      <Minus className="w-4 h-4" data-id="element-2603" data-mp="u2ekcbhsk0f8" />
                    </button>
                    <span className="font-bold text-sm w-5 text-center" data-id="element-2604" data-mp="1hqfo7740ysob">
                      {line.quantity}
                    </span>
                    <button type="button" onClick={() => handleIncrement(item)} className="w-8 h-8 rounded-full bg-brand-amber text-white flex items-center justify-center hover:bg-brand-amber-hover transition-colors" aria-label="Aumentar cantidad" data-id="element-2605" data-mp="1mpihxvek1cgz">
                      <Plus className="w-4 h-4" data-id="element-2606" data-mp="2aqapwqeds03" />
                    </button>
                  </div> : <button type="button" onClick={() => handleQuickAdd(item)} className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-brand-amber text-white text-xs font-bold hover:bg-brand-amber-hover transition-colors" data-id="element-2607" data-mp="t1bop81xp7jt">
                    <Plus className="w-3.5 h-3.5" data-id="element-2608" data-mp="f2c183q5qyp8" />
                    Agregar
                  </button>}
              </div>;
        })}
        </div>
      </div>;
  };
  if (!isOpen) return null;
  return <>
      <AnimatePresence data-id="element-2609" data-mp="12y6orvduvg07">
        <motion.div key="upsell-backdrop" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-id="element-2610" data-mp="12xer8f0p787a">
          <motion.div key="upsell-card" initial={{
          y: 40,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 40,
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 28,
          stiffness: 320
        }} onClick={e => e.stopPropagation()} className="relative w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col max-h-[92vh] overflow-hidden" data-id="element-2611" data-mp="1xzyvqu218yqm">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 px-5 pt-5 pb-3 border-b border-gray-100 shrink-0" data-id="element-2612" data-mp="2qq77o45z6mwr">
              <div className="min-w-0" data-id="element-2613" data-mp="30maehnbrv9q1">
                <h2 className="text-lg font-bold text-gray-900" data-id="element-2614" data-mp="30nlkzuwlqvku">{title}</h2>
                <p className="text-sm text-gray-500 mt-0.5" data-id="element-2615" data-mp="34mugp5b7d3j1">{subtitle}</p>
              </div>
              <button type="button" onClick={onClose} className="shrink-0 p-2 -mr-2 -mt-1 text-gray-400 hover:text-gray-700 rounded-full hover:bg-gray-50" aria-label="Cerrar" data-id="element-2616" data-mp="ffnpcpnhlp6o">
                <X className="w-5 h-5" data-id="element-2617" data-mp="paz95mdr5mla" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-5 py-4" data-id="element-2618" data-mp="u5lg9dcswszk">
              {loading ? <div className="flex items-center justify-center py-12" data-id="element-2619" data-mp="z57vttg3qlbj">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" data-id="element-2620" data-mp="132vv18otv098" />
                </div> : Object.keys(grouped).length === 0 ? <p className="text-sm text-gray-500 text-center py-8" data-id="element-2621" data-mp="1d3690viosbpg">
                  No hay productos disponibles ahora.
                </p> : <>
                  {categorySlugs.map(slug => {
                const cat = categories.find(c => c.slug === slug);
                return cat ? renderSection(slug, cat.name) : null;
              })}
                </>}
            </div>

            {/* Footer */}
            <div className="px-5 py-4 border-t border-gray-100 bg-white shrink-0 space-y-2" data-id="element-2622" data-mp="32hxhvmr14o6c">
              <Button onClick={onContinue} className="w-full h-12 text-base font-bold" data-id="element-2623" data-mp="2xice8z0wzrna">
                Continuar al checkout
              </Button>
              <button type="button" onClick={onContinue} className="w-full h-11 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors" data-id="element-2624" data-mp="3q706xfnztjav">
                No gracias, continuar
              </button>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>

      {/* Reuse the existing customization modal for customizable items */}
      {productModalItem && <ProductDetailModal isOpen={!!productModalItem} onClose={() => setProductModalItem(null)} item={productModalItem} onAdd={handleProductModalAdd} data-id="element-2625" data-mp="1zvdf8hmr27m8" />}
    </>;
}